import { useEffect } from "react";
import { Box, Stack } from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  DataTable, EmptyRow, Prompt, Screen, StatusDot, type Column,
  LoadError,
}  from "../components/primitives";
import { useConsoleStore } from "../stores/consoleStore";
import { useWorkspaceStore } from "../stores/workspaceStore";
import { bar } from "../lib/vf";
import type { CompactionEntry } from "../types/domain";
import css from "./screens.module.css";

const COLUMNS: Column[] = [
  { key: "namespace", label: "namespace", width: "2fr" },
  { key: "pressure", label: "pressure", width: "2fr" },
  { key: "deltas", label: "deltas", width: "0.6fr" },
  { key: "status", label: "status", width: "0.8fr" },
];

export default function Compaction() {
  const workspaceId = useWorkspaceStore((s) => s.currentId);
  const compaction = useConsoleStore((s) => s.compaction);
  const loadError = useConsoleStore((s) => s.errors.compaction);
  const trash = useConsoleStore((s) => s.trash);
  const loadCompaction = useConsoleStore((s) => s.loadCompaction);
  const loadTrash = useConsoleStore((s) => s.loadTrash);
  const restoreTrash = useConsoleStore((s) => s.restoreTrash);
  const purgeTrash = useConsoleStore((s) => s.purgeTrash);

  useEffect(() => {
    if (!workspaceId) return;
    void loadCompaction();
    void loadTrash();
  }, [workspaceId, loadCompaction, loadTrash]);

  const confirmRestore = (namespace: string, size: string) =>
    modals.openConfirmModal({
      title: "restore namespace",
      labels: { confirm: "restore", cancel: "cancel" },
      children: (
        <Box fz="12.5px" c="var(--vf-sec)" lh={1.7}>
          <Box c="var(--vf-ok)" mb={10}>$ vf trash restore</Box>
          Rehydrate {namespace} ({size}) from trash. The base index and delta segments are
          re-attached and queries resume immediately.
        </Box>
      ),
      onConfirm: () => {
        void restoreTrash(namespace).then(() =>
          notifications.show({ message: `restored ${namespace}`, color: "green" }),
        );
      },
    });

  const confirmPurge = (namespace: string, size: string) =>
    modals.openConfirmModal({
      title: "purge permanently",
      labels: { confirm: "purge forever", cancel: "cancel" },
      confirmProps: { color: "red" },
      children: (
        <Box fz="12.5px" c="var(--vf-sec)" lh={1.7}>
          <Box c="var(--vf-bad)" mb={10}>$ vf trash purge --force</Box>
          Permanently erase {namespace} ({size}) from object storage. This cannot be undone and frees
          the storage immediately.
        </Box>
      ),
      onConfirm: () => {
        void purgeTrash(namespace).then(() =>
          notifications.show({ message: `purged ${namespace}`, color: "red" }),
        );
      },
    });

  return (
    <Screen>
      <Prompt command="vf compact status && vf gc ls" />
      <LoadError message={loadError} />

      <div className={css.sectionNote}>
        // compaction queue — pressure = delta bytes / base bytes
      </div>
      <Box mb={22}>
        <DataTable<CompactionEntry>
          fontSize="12.5px"
          columns={COLUMNS}
          rows={compaction}
          rowKey={(c) => c.namespace}
          empty={
            <EmptyRow
              title="nothing to compact"
              body="Delta segments accumulate as you upsert. When their size approaches the base index, compaction is scheduled automatically."
            />
          }
          renderCell={(c, key) => {
            switch (key) {
              case "namespace":
                return <span style={{ color: "var(--vf-text)" }}>{c.namespace}</span>;
              case "pressure":
                // The mockup only draws the bar once pressure passes 0.75;
                // below that the percentage alone carries the signal.
                return (
                  <span>
                    {c.pressure > 0.75 ? (
                      <span className={css.pressureBar}>{bar(c.pressure, 20)}</span>
                    ) : null}
                    <span style={{ color: "var(--vf-sec)" }}> {Math.round(c.pressure * 100)}%</span>
                  </span>
                );
              case "deltas":
                return <span style={{ color: "var(--vf-sec)" }}>{c.deltas}</span>;
              case "status":
                return <StatusDot status={c.status} />;
              default:
                return null;
            }
          }}
        />
      </Box>

      <div className={css.sectionNote}>
        // pending deletions — .gc-pending markers, 7-day grace before reaping
      </div>

      {trash.length === 0 ? (
        <Box
          p={24}
          ta="center"
          fz="12.5px"
          c="var(--vf-dim)"
          style={{ border: "1px dashed var(--vf-border)", background: "var(--vf-panel)" }}
        >
          trash is empty — no namespaces pending deletion
        </Box>
      ) : (
        <Stack gap={10}>
          {trash.map((t) => (
            <div key={t.namespace} className={css.trashRow}>
              <div className={css.trashMeta}>
                <div className={css.trashNs}>{t.namespace}</div>
                <div className={css.trashSub}>
                  {t.size} · expires {t.expires}
                </div>
              </div>
              <button
                type="button"
                className={css.restoreBtn}
                onClick={() => confirmRestore(t.namespace, t.size)}
              >
                restore
              </button>
              <button
                type="button"
                className={css.dangerBtn}
                onClick={() => confirmPurge(t.namespace, t.size)}
              >
                purge now
              </button>
            </div>
          ))}
        </Stack>
      )}
    </Screen>
  );
}
